import React, { useState, useEffect, useRef } from 'react';
import "../styles/navbar.css";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from '../routes/path';
import { useAuth } from "../utils/useAuth";
import { useLanguage } from "../utils/LanguageContext";
import { useCart } from "../utils/CartContext";
import api from "../services/api";
import { useScrolled } from "../utils/motion";

const FLAGS = {
  en: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Flag_of_the_United_States.svg/960px-Flag_of_the_United_States.svg.png',
  ru: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f3/Flag_of_Russia.svg/960px-Flag_of_Russia.svg.png',
  uz: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/84/Flag_of_Uzbekistan.svg/960px-Flag_of_Uzbekistan.svg.png',
};
const LANGS = ['en', 'ru', 'uz'];

export default function Navbar() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, changeLanguage } = useLanguage();
  const { cartCount } = useCart();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const langRef = useRef(null);
  const scrolled = useScrolled(12);

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLangOpen(false);
  }, [location.pathname]);

  // Close language menu on outside click / Escape
  useEffect(() => {
    if (!isLangOpen) return undefined;
    const onDown = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setIsLangOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setIsLangOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isLangOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(e.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleLogout = async () => {
    try {
      await api.post("logout/");
      localStorage.removeItem('authToken');
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
      window.location.href = "/";
    }
  };

  return (
    <nav className={`site-nav ${scrolled ? 'is-scrolled' : ''} ${isMobileMenuOpen ? 'menu-open' : ''}`}>
      <div className="nav-container">
        <Link to="/" className="logo" aria-label="EcoNovda">
          <span className="logo-mark" aria-hidden="true">
            <i className="fas fa-seedling" />
          </span>
          <span className="logo-word">EcoNovda<span className="logo-dot">.</span></span>
        </Link>

        {/* DESKTOP LINKS */}
        <ul className="nav-links">
          <li><NavLink end to="/">{t('nav_home')}</NavLink></li>
          <li><NavLink to={`/${ROUTES.SHOP}`}>{t('nav_shop')}</NavLink></li>
          <li><NavLink to={`/${ROUTES.MAP}`}>{t('nav_map')}</NavLink></li>
          <li><NavLink to={`/${ROUTES.COMMUNITY}`}>{t('nav_community')}</NavLink></li>
        </ul>

        <div className="nav-cta">
          {/* Language Selector */}
          <div className={`lang-selector-container ${isLangOpen ? 'open' : ''}`} ref={langRef}>
            <button
              type="button"
              onClick={() => setIsLangOpen(o => !o)}
              className="lang-btn"
              aria-haspopup="listbox"
              aria-expanded={isLangOpen}
            >
              <span className="flag-icon">
                <img src={FLAGS[language] || FLAGS.uz} alt="" />
              </span>
              <span className="lang-text">{language.toUpperCase()}</span>
              <i className="fas fa-chevron-down lang-chevron" />
            </button>
            <div className="lang-dropdown" role="listbox">
              {LANGS.map(code => (
                <button
                  type="button"
                  key={code}
                  role="option"
                  aria-selected={language === code}
                  onClick={() => { changeLanguage(code); setIsLangOpen(false); }}
                  className={`lang-option ${language === code ? 'active' : ''}`}
                >
                  <span className="flag-icon-sm">
                    <img src={FLAGS[code]} alt="" />
                  </span>
                  <span>{code.toUpperCase()}</span>
                  {language === code && <i className="fas fa-check lang-check" />}
                </button>
              ))}
            </div>
          </div>

          {isAuthenticated ? (
            <>
              {/* Cart Icon */}
              <div
                className="cart-icon-wrapper"
                onClick={() => navigate(`/${ROUTES.CART}`)}
                role="button"
                tabIndex={0}
                title="Cart"
                aria-label="Cart"
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/${ROUTES.CART}`)}
              >
                <i className="fas fa-shopping-cart"></i>
                {cartCount > 0 && (
                  <span className="cart-badge" key={cartCount}>
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </div>

              {/* Orders Icon */}
              <div
                className="cart-icon-wrapper"
                onClick={() => navigate(`/${ROUTES.ORDERS}`)}
                role="button"
                tabIndex={0}
                title="My Orders"
                aria-label="My Orders"
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/${ROUTES.ORDERS}`)}
              >
                <i className="fas fa-receipt"></i>
              </div>

              {/* Desktop Auth Buttons */}
              <div className="desktop-auth-btns">
                <button
                  className="btn-register"
                  onClick={() => navigate(`/${ROUTES.PROFILE}`)}
                >
                  {t('nav_profile')}
                </button>
                <button
                  className="btn-logout"
                  onClick={handleLogout}
                  aria-label={t('nav_logout')}
                  title={t('nav_logout')}
                >
                  <i className="fa-solid fa-right-from-bracket"></i>
                </button>
              </div>
            </>
          ) : (
            <div className="desktop-auth-btns">
              <Link className="btn-signin" to={`/${ROUTES.AUTH}?mode=login`}>
                {t('nav_signin')}
              </Link>
              <Link className="btn-register" to={`/${ROUTES.AUTH}?mode=register`}>
                {t('nav_register')}
              </Link>
            </div>
          )}

          {/* HAMBURGER BUTTON */}
          <button
            className={`hamburger-btn ${isMobileMenuOpen ? 'is-open' : ''}`}
            ref={hamburgerRef}
            onClick={toggleMobileMenu}
            aria-label="Menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="hamburger-lines" aria-hidden="true"><span /><span /><span /></span>
          </button>
        </div>
      </div>

      {/* MOBILE DROPDOWN MENU */}
      <div ref={mobileMenuRef} className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-links">
          <li><NavLink end to="/" onClick={closeMobileMenu}><i className="fas fa-house" />{t('nav_home')}</NavLink></li>
          <li><NavLink to={`/${ROUTES.SHOP}`} onClick={closeMobileMenu}><i className="fas fa-store" />{t('nav_shop')}</NavLink></li>
          <li><NavLink to={`/${ROUTES.MAP}`} onClick={closeMobileMenu}><i className="fas fa-map-location-dot" />{t('nav_map')}</NavLink></li>
          <li><NavLink to={`/${ROUTES.COMMUNITY}`} onClick={closeMobileMenu}><i className="fas fa-people-group" />{t('nav_community')}</NavLink></li>
        </ul>

        <div className="mobile-auth-section">
          {isAuthenticated ? (
            <>

              <button
                className="btn-mobile-profile"
                onClick={() => { navigate(`/${ROUTES.ORDERS}`); closeMobileMenu(); }}
              >
                <i className="fa-solid fa-receipt"></i> {t('orders_title') || 'My Orders'}
              </button>
              <button
                className="btn-mobile-profile"
                onClick={() => { navigate(`/${ROUTES.PROFILE}`); closeMobileMenu(); }}
              >
                <i className="fa-solid fa-user"></i> {t('nav_profile')}
              </button>
              <button
                className="btn-mobile-logout"
                onClick={() => { handleLogout(); closeMobileMenu(); }}
              >
                <i className="fa-solid fa-right-from-bracket"></i> {t('nav_logout')}
              </button>
            </>
          ) : (
            <div className="mobile-auth-buttons">
              <Link className="btn-mobile-signin" to={`/${ROUTES.AUTH}?mode=login`} onClick={closeMobileMenu}>
                {t('nav_signin')}
              </Link>
              <Link className="btn-mobile-register" to={`/${ROUTES.AUTH}?mode=register`} onClick={closeMobileMenu}>
                {t('nav_register')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}